import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

/* ── Types ─────────────────────────────────────────────── */

export type ToastType = 'success' | 'error' | 'loading' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  /** Timestamp when the toast was first created (ms since epoch) */
  createdAt: number;
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id' | 'createdAt'>) => string;
  removeToast: (id: string) => void;
  removeAll: () => void;
}

/* ── Context ───────────────────────────────────────────── */

const Ctx = createContext<ToastContextValue | null>(null);

export function useToastContext(): ToastContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

/* ── Provider ───────────────────────────────────────────── */

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((t: Omit<Toast, 'id' | 'createdAt'>): string => {
    const id = crypto.randomUUID();
    const createdAt = Date.now();
    setToasts((prev) => {
      /* Deduplicate: if an identical message+type already exists, refresh
       * its duration but keep the original createdAt so the remaining
       * display time is correct. */
      const duplicate = prev.find(
        (existing) => existing.message === t.message && existing.type === t.type,
      );
      if (duplicate) {
        return prev.map((existing) =>
          existing.id === duplicate.id ? { ...existing, duration: t.duration, createdAt } : existing,
        );
      }
      return [...prev, { ...t, id, createdAt }];
    });
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const removeAll = useCallback(() => setToasts([]), []);

  return (
    <Ctx.Provider value={{ addToast, removeToast, removeAll }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </Ctx.Provider>
  );
}

/* ── Container ──────────────────────────────────────────── */

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  /* Only show the most recent toasts; older ones still auto-dismiss */
  const visible = toasts.slice(-MAX_VISIBLE_TOASTS);

  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none"
    >
      {visible.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

/* ── Item ───────────────────────────────────────────────── */

const MAX_VISIBLE_TOASTS = 3;

const TYPE_STYLE: Record<ToastType, string> = {
  success: 'bg-emerald-600 text-white',
  error: 'bg-red-600 text-white',
  loading: 'bg-blue-600 text-white',
  info: 'bg-slate-700 text-white',
};

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);

  /* enter animation — rAF ensures the initial frame paints first */
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  /* auto-dismiss: compute remaining time from createdAt */
  useEffect(() => {
    if (toast.duration > 0 && toast.duration !== Infinity) {
      const elapsed = Date.now() - toast.createdAt;
      const remaining = Math.max(0, toast.duration - elapsed);
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => onDismiss(toast.id), 300);
      }, remaining);
      return () => clearTimeout(timer);
    }
  }, [toast.id, toast.duration, toast.createdAt, onDismiss]);

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div
      role="status"
      className={[
        'px-4 py-3 rounded-lg shadow-lg text-sm font-medium max-w-sm',
        'flex items-center gap-3 pointer-events-auto',
        'transition-all duration-300 ease-out',
        TYPE_STYLE[toast.type],
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4',
      ].join(' ')}
    >
      {toast.type === 'loading' && (
        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
      )}
      <span className="flex-1 min-w-0 break-words">{toast.message}</span>
      <button
        type="button"
        onClick={dismiss}
        className="opacity-60 hover:opacity-100 transition-opacity text-xs flex-shrink-0 leading-none"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
